import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight, Download, HandCoins } from 'lucide-react';
import type { GiveawayKind, GiveawayRow } from '@sebastian/shared';
import { formatRate } from '@sebastian/shared';
import { api } from '../lib/api';
import { POLL } from '../hooks/queries';
import { useCurrency } from '../lib/currency';
import { csvDateTime, downloadCsv } from '../lib/csv';
import { formatClock, formatDate } from '../lib/format';
import { Badge, Button, Card, EmptyState, ErrorState, Input, Spinner, Stat } from '../components/ui';

const KIND_META: Record<GiveawayKind, { label: string; tone: 'danger' | 'caution' | 'info' | 'accent' }> = {
  ITEM_DISCOUNT: { label: 'Item discount', tone: 'caution' },
  BILL_DISCOUNT: { label: 'Bill discount', tone: 'info' },
  VOIDED_ITEM: { label: 'Voided', tone: 'danger' },
  INVITED_TABLE: { label: 'Invited table', tone: 'accent' },
};

/**
 * The business day in progress, matching the Worker's 05:00 → 05:00 definition
 * (`sebasDayWindow`): before 05:00 the night that is still running started
 * yesterday. The till sits in the restaurant, so its own clock is the
 * restaurant's clock.
 */
function currentBusinessDate(now = new Date()): string {
  const shifted = new Date(now);
  if (shifted.getHours() < 5) shifted.setDate(shifted.getDate() - 1);
  return [
    shifted.getFullYear(),
    String(shifted.getMonth() + 1).padStart(2, '0'),
    String(shifted.getDate()).padStart(2, '0'),
  ].join('-');
}

function shiftDate(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number);
  const shifted = new Date(year!, month! - 1, day! + days);
  return [
    shifted.getFullYear(),
    String(shifted.getMonth() + 1).padStart(2, '0'),
    String(shifted.getDate()).padStart(2, '0'),
  ].join('-');
}

/** What a row was: the dish, or the whole bill. */
function describe(row: GiveawayRow): string {
  if (row.itemName) return `${row.quantity ?? 1}× ${row.itemName}`;
  return row.kind === 'INVITED_TABLE' ? 'Whole table — on the house' : 'Whole bill';
}

/**
 * One evening's complimentary items and discounts.
 *
 * Everything that left the till without being takings, on one page: a dish
 * discounted, a bill discounted, a dish voided after it was fired, and a table
 * eating on the house. They are listed together because they cost the same.
 *
 * Kept off the Reports screen on purpose — that one is read as a range and a
 * trend, this one is read one night at a time at close.
 */
export function GiveawaysPage() {
  const { money } = useCurrency();
  const [date, setDate] = useState(currentBusinessDate);
  const tonight = currentBusinessDate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['giveaways', date],
    queryFn: () => api.reports.giveaways(date),
    // Tonight's figures move as service runs; an earlier night never changes.
    refetchInterval: date === tonight ? POLL.admin : false,
  });

  const totals = data?.totals;
  const rows = data?.rows ?? [];

  const byKind = useMemo(
    () =>
      [
        { kind: 'ITEM_DISCOUNT' as const, cents: totals?.itemDiscountCents ?? 0, count: totals?.itemDiscountCount ?? 0 },
        { kind: 'BILL_DISCOUNT' as const, cents: totals?.billDiscountCents ?? 0, count: totals?.billDiscountCount ?? 0 },
        { kind: 'VOIDED_ITEM' as const, cents: totals?.voidedCents ?? 0, count: totals?.voidedCount ?? 0 },
        { kind: 'INVITED_TABLE' as const, cents: totals?.invitedCents ?? 0, count: totals?.invitedCount ?? 0 },
      ],
    [totals],
  );

  function exportCsv() {
    downloadCsv(`giveaways-${date}.csv`, [
      ['Time', 'Table', 'Order', 'Kind', 'What', 'Quantity', 'Discount', 'Amount', 'Reason', 'Staff'],
      ...rows.map((row) => [
        csvDateTime(row.at),
        row.tableNumber,
        `#${row.orderNumber}`,
        KIND_META[row.kind].label,
        row.itemName ?? describe(row),
        row.quantity ?? '',
        row.discountType === 'PERCENT' ? `${formatRate(row.discountValue)}%` : '',
        (row.amountCents / 100).toFixed(2),
        row.reason ?? '',
        row.staffName ?? '',
      ]),
    ]);
  }

  return (
    <div className="mx-auto max-w-[1400px] px-5 py-6">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-wide text-ink">Giveaways</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-faint">
            Everything the house gave away tonight — discounted dishes, discounted bills, dishes
            voided after they were fired, and tables on the house. A night runs 05:00 to 05:00, so a
            table seated after midnight stays on the night it started.
          </p>
        </div>
        <Button variant="secondary" icon={<Download size={16} />} disabled={rows.length === 0} onClick={exportCsv}>
          Export CSV
        </Button>
      </header>

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <Button variant="secondary" aria-label="Previous night" onClick={() => setDate(shiftDate(date, -1))}>
            <ChevronLeft size={18} />
          </Button>
          <div className="min-w-44 px-2 text-center">
            <div className="font-display text-lg leading-tight text-ink">{formatDate(`${date}T12:00:00`)}</div>
            <div className="text-[11px] uppercase tracking-wider text-ink-faint">
              {date === tonight ? 'Tonight — still running' : 'Closed night'}
            </div>
          </div>
          <Button
            variant="secondary"
            aria-label="Next night"
            disabled={date >= tonight}
            onClick={() => setDate(shiftDate(date, 1))}
          >
            <ChevronRight size={18} />
          </Button>
        </div>

        {date !== tonight ? (
          <Button variant="ghost" onClick={() => setDate(tonight)}>
            Back to tonight
          </Button>
        ) : null}

        <Input
          type="date"
          max={tonight}
          className="w-auto"
          value={date}
          onChange={(event) => event.target.value && setDate(event.target.value)}
        />
      </div>

      {isLoading ? <Spinner label="Adding up the night…" /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      {totals ? (
        <>
          <Card className="mb-5">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-widest text-ink-faint">
                  Given away
                </div>
                <div
                  className={clsx(
                    'tnum mt-1 font-display text-4xl leading-none',
                    totals.totalCents > 0 ? 'text-danger' : 'text-ink',
                  )}
                >
                  {money(totals.totalCents)}
                </div>
              </div>
              <div className="flex flex-wrap items-end gap-8">
                {byKind.map((entry) => (
                  <Stat
                    key={entry.kind}
                    label={`${KIND_META[entry.kind].label}${entry.count > 0 ? ` (${entry.count})` : ''}`}
                    value={money(entry.cents)}
                  />
                ))}
              </div>
            </div>
          </Card>

          {rows.length === 0 ? (
            <EmptyState
              icon={<HandCoins size={30} />}
              title="Nothing given away"
              message="No discounts, no voided dishes and no invited tables on this night."
            />
          ) : (
            <Card padded={false} className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="bg-raised/60 text-left text-[11px] uppercase tracking-wider text-ink-faint">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Time</th>
                    <th className="px-4 py-3 font-semibold">Table</th>
                    <th className="px-4 py-3 font-semibold">What</th>
                    <th className="px-4 py-3 font-semibold">Kind</th>
                    <th className="px-4 py-3 font-semibold">Reason</th>
                    <th className="px-4 py-3 font-semibold">By</th>
                    <th className="px-4 py-3 text-right font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const meta = KIND_META[row.kind];
                    return (
                      <tr key={`${row.orderId}-${row.kind}-${index}`} className="border-t border-line hover:bg-hover">
                        <td className="tnum px-4 py-3 text-ink-faint">{formatClock(row.at)}</td>
                        <td className="px-4 py-3">
                          <span className="font-display text-lg text-ink">
                            {String(row.tableNumber).padStart(2, '0')}
                          </span>
                          <span className="ml-2 text-xs text-ink-faint">#{row.orderNumber}</span>
                        </td>
                        <td className="px-4 py-3 text-ink">
                          {describe(row)}
                          {row.discountType === 'PERCENT' ? (
                            <span className="ml-1.5 text-xs text-ink-faint">
                              ({formatRate(row.discountValue)}%)
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <Badge tone={meta.tone}>{meta.label}</Badge>
                        </td>
                        <td className="max-w-64 px-4 py-3 text-xs text-ink-soft">{row.reason ?? '—'}</td>
                        <td className="px-4 py-3 text-xs text-ink-faint">{row.staffName ?? '—'}</td>
                        <td className="tnum px-4 py-3 text-right font-semibold text-danger">
                          −{money(row.amountCents)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}

          <p className="mt-4 text-xs text-ink-faint">
            Figures are internal and appear on no guest's bill. A voided dish counts only once it had
            been sent to the kitchen or bar — a line taken off before it was fired cost nothing.
          </p>
        </>
      ) : null}
    </div>
  );
}
